import { useTheme } from "@/context/ThemeContext";

const outcomes = [
  { icon: "ri-calendar-2-line", text: "More booked appointments", detail: "Fill your calendar automatically" },
  { icon: "ri-timer-flash-line", text: "Faster response times", detail: "Under 60 seconds, every time" },
  { icon: "ri-arrow-up-circle-line", text: "Higher conversion rates", detail: "Turn more leads into clients" },
  { icon: "ri-user-settings-line", text: "Reduced admin workload", detail: "Automate the repetitive tasks" },
  { icon: "ri-shield-check-line", text: "Consistent client experience", detail: "Every touchpoint, on brand" },
];

export default function OutcomesSection() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section className={`py-32 md:py-44 transition-colors duration-300 ${isDark ? "bg-[#0a0a0a]" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left: Text + outcomes */}
          <div>
            <p className={`text-[11px] tracking-[0.15em] uppercase font-semibold mb-5 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Built for Results</p>
            <h2 className={`text-4xl md:text-5xl font-black leading-[1.08] tracking-[-0.02em] mb-12 ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>
              Our systems are built for results.
            </h2>

            <div className="flex flex-col gap-2">
              {outcomes.map((outcome) => (
                <div
                  key={outcome.text}
                  className={`flex items-center gap-5 p-5 rounded-2xl transition-all duration-200 group cursor-default ${
                    isDark ? "hover:bg-white/5" : "hover:bg-[#f5f5f7]"
                  }`}
                >
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/8" : "bg-[#f5f5f7]"}`}>
                    <i className={`${outcome.icon} text-xl ${isDark ? "text-white/75" : "text-[#1d1d1f]/75"}`}></i>
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold text-base ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>{outcome.text}</p>
                    <p className={`text-sm ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>{outcome.detail}</p>
                  </div>
                  <i className={`ri-arrow-right-line text-sm transition-opacity duration-200 opacity-0 group-hover:opacity-100 ${isDark ? "text-white/40" : "text-[#1d1d1f]/40"}`}></i>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-square">
              <img
                src="https://public.readdy.ai/ai/img_res/aed18ed6092b997d627ea220112b0084.jpg"
                alt="Business outcomes"
                className="w-full h-full object-cover object-top"
              />
              <div className={`absolute inset-0 rounded-3xl ${isDark ? "bg-gradient-to-br from-transparent to-[#0a0a0a]/50" : "bg-gradient-to-br from-transparent to-black/20"}`}></div>
            </div>

            {/* Floating metric cards */}
            <div className={`absolute top-8 -left-6 rounded-2xl p-4 backdrop-blur-md border ${isDark ? "bg-[#1c1c1e]/90 border-white/12" : "bg-white/95 border-black/8"}`}>
              <p className={`text-[10px] tracking-widest uppercase font-semibold mb-1 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Avg Response</p>
              <p className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>&lt;60s</p>
            </div>

            <div className={`absolute bottom-8 -right-6 rounded-2xl p-4 backdrop-blur-md border ${isDark ? "bg-[#1c1c1e]/90 border-white/12" : "bg-white/95 border-black/8"}`}>
              <p className={`text-[10px] tracking-widest uppercase font-semibold mb-1 ${isDark ? "text-white/35" : "text-[#6e6e73]"}`}>Conversion Lift</p>
              <p className={`text-2xl font-black ${isDark ? "text-white" : "text-[#1d1d1f]"}`}>+55%</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
